/*  
<summary>
  Functional Component: CustomerList
  Developer: Sahil Bharti
  Created Date: 06-Jan-2025
  Description: Renders a Customer management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the Customer Management interface</returns>
*/

import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CellContext } from "@tanstack/react-table";
import _ from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import noMessageIcon from "../../assets/Images/svg/018-empty.svg";
import { integerRegex, stringJpRegex } from "../../constants/constants";
import { ActionType } from "../../constants/enums/action-type-enum";
import { ColumnType } from "../../constants/enums/grid-column-type-enum";
import { ModuleType } from "../../constants/enums/module-type-enum";
import userSettingEnum from "../../constants/enums/user-setting-enum";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import { customerInitialFilterState } from "../../core/initialState/initial-filter-state";
import { isOperationPermittedRoleBase } from "../../helpers/action-permission-helper";
import {
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import { toHalfWidth } from "../../helpers/validate-helper";
import IPageInfo, { IPageLimitInfo } from "../../models/ICommon";
import { ISorting } from "../../models/ISorting";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import Grid, { getColumns } from "../../shared-components/Grid/Grid";
import Pagination from "../../shared-components/Grid/Pagination";
import LoaderButton from "../../shared-components/button/loader-button";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import AddPopupHOC from "../../shared-components/popup/add/add-popup-hoc";
import DeletePopup from "../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../translations/format-message";
import RoutesConstants from "../routes.constant";
import AddEditCustomerManagement from "./components/add-edit-customer-management";
import AdvanceSearchCustomer from "./components/advance-search-customer-management";
import "./customer-management.css";
import AdvanceSearchPopupHOC from "./hoc/advance-search-popup-hoc";

const CustomerManagement: React.FC = (props: any) => {
  const {
    optionMenuStore,
    preferencesStore,
    masterManagementStore,
    customerManagementStore,
    smrStore,
  } = useStore();
  const location = useLocation();
  const history = useHistory();

  const [openPanel, setOpenPanel] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const {
    inProgress,
    error,
    GetCustomerListService,
    GetSearchCustomerRegistrationFilterService,
    GetSearchCustomerRegistrationFilterListService,
    allCustomer,
    customerList,
    resetAddUpdateCustomerState,
    addUpdateCustomerState,
    deleteCustomerState,
    DeleteCustomerService,
    resetDeleteCustomer,
    resetCustomerDetail,
    getCustomerFilter,
    resetCustomerFilterDetail,
    setCustomerFilterDetail,
    AdvanceSearchCustomerRegistrationService,
    advanceSearchValues,
    setAdvanceSearchValues,
    resetAdvanceSearchValues,
    advanceSearchState
  } = customerManagementStore;
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;

  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [kanaInput, setKanaInput] = useState("");
  const [firstNameKanaSearch, setFirstNameKanaSearch] = useState(
    customerInitialFilterState.FirstNameKana ?? ""
  );
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSearch, setPhoneSearch] = useState(
    customerInitialFilterState.PhoneNumber ?? ""
  );

  const [showInvalidStores, setShowInvalidStores] = useState<boolean | null>(
    customerInitialFilterState.IsBlocked
  );

  let customerId =
    (location.state as { customerId?: number })?.customerId ?? -1;
  const [customerIdNo, setCustomerIdNo] = useState<number>(customerId);

  const [freeWordSearch, setFreeWordSearch] = useState("");
  const [searchInput, setSearchInput] = useState(freeWordSearch);
  const [customerCodeInput, setCustomerCodeInput] = useState("");
  const [searchCustomerCodeInput, setSearchCustomerCodeInput] = useState("");
  const userTypefromLocalStorage = getUserTypeFromLocalStorage();

  // Fuction to handle invaiid treatment filter
  const handleInvalidTreatmentFilter = useCallback(() => {
    setCustomerFilterDetail({
      ...customerInitialFilterState,
      IsBlocked: !showInvalidStores,
      IsFilterActive: true,
    });
    setShowInvalidStores((prev) => !prev);
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  }, []);

  // Function to handle free word search
  const handleSearchClick = () => {
    const searchQuery = searchInput.trim();
    if (searchQuery.length === 0) {
      setFreeWordSearch("");
      setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
      return;
    }
    setCustomerFilterDetail({
      ...customerInitialFilterState,
      SearchQuery: searchQuery,
      IsFilterActive: true,
    });
    setFreeWordSearch(searchQuery);
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  };

  // function to handle phone search
  const handlePhoneSearch = () => {
    const trimmedPhone = phoneInput.trim();
    if (trimmedPhone.length === 0) {
      setPhoneSearch("");
      setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
      return;
    }
    setCustomerFilterDetail({
      ...customerInitialFilterState,
      PhoneNumber: trimmedPhone,
      IsFilterActive: true,
    });
    setPhoneSearch(trimmedPhone);
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  };

  // Funtion to handle kanana name search
  const handleKanaSearch = () => {
    const trimmedKana = kanaInput.trim();
    if (trimmedKana.length === 0) {
      setFirstNameKanaSearch("");
      setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
      return;
    }
    setCustomerFilterDetail({
      ...customerInitialFilterState,
      FirstNameKana: trimmedKana,
      IsFilterActive: true,
    });
    setFirstNameKanaSearch(trimmedKana);
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  };
  const handleCustomerCodeSearch = () => {
    const trimmedCustomerCode = customerCodeInput.trim();
    if (trimmedCustomerCode.length === 0) {
      setSearchCustomerCodeInput("");
      setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
      return;
    }
    setCustomerFilterDetail({
      ...customerInitialFilterState,
      CustomerCode: trimmedCustomerCode,
      IsFilterActive: true,
    });
    setSearchCustomerCodeInput(trimmedCustomerCode);
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  };

  useEffect(() => {
    if (
      searchInput.trim() === "" &&
      phoneInput.trim() === "" &&
      kanaInput.trim() === "" &&
      customerCodeInput.trim() === ""
    ) {
      setFreeWordSearch("");
      setPhoneSearch("");
      setFirstNameKanaSearch("");
      setSearchCustomerCodeInput("");
      setCustomerFilterDetail(customerInitialFilterState);
    } else {
      if (searchInput.trim() === "") setFreeWordSearch("");
      if (phoneInput.trim() === "") setPhoneSearch("");
      if (kanaInput.trim() === "") setFirstNameKanaSearch("");
      if (customerCodeInput.trim() === "") setSearchCustomerCodeInput("");
    }
  }, [
    searchInput,
    phoneInput,
    kanaInput,
    customerCodeInput,
    setCustomerFilterDetail,
  ]);

  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const [sorting, setSorting] = useState<ISorting>({
    column: "last_name_kana",
    orderBy: "asc",
  });

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    let pages: number;
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pageInfo.totalPages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  // function to handle sorting on grid
  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  /**
   * This function shows the add user pop up from where we can add new user and update existing user. we use id to identify
   * that we are adding or updating user.
   * "Id == -1" : we are adding new user
   * "Id != -1" : means we are updating existing user
   */
  const openAddCustomerHandler = (row: { Id: number }) => {
    resetCustomerDetail();
    setSelectedId(row.Id);
    props.addToggleHandler();
  };

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */
  const callGetCustomerListService = useCallback(() => {
    if(!advanceSearchValues.isAdvanceSearchActive){
      const searchQuery = freeWordSearch.trim();
      const kanaQuery = firstNameKanaSearch.trim();
      const phoneQuery = toHalfWidth(phoneSearch.trim());
      const customerCodeQuery = searchCustomerCodeInput.trim();
      GetSearchCustomerRegistrationFilterService(
        pageInfo.currentPage,
        pageLimitInfo.pageLimit,
        searchQuery,
        showInvalidStores,
        kanaQuery,
        phoneQuery,
        sorting.orderBy,
        sorting.column,
        customerCodeQuery
      );
    }
    else{
      AdvanceSearchCustomerRegistrationService( pageInfo.currentPage,
        pageLimitInfo.pageLimit, _.omit(advanceSearchValues, "isAdvanceSearchActive"));
    }
  }, [
    freeWordSearch,
    firstNameKanaSearch,
    phoneSearch,
    searchCustomerCodeInput,
    GetSearchCustomerRegistrationFilterService,
    pageInfo.currentPage,
    pageLimitInfo.pageLimit,
    showInvalidStores,
    sorting.orderBy,
    sorting.column,
    advanceSearchValues
  ]);

  const callCSVOutput = () => {
        // if(!advanceSearchValues.isAdvanceSearchActive){
      const searchQuery = freeWordSearch.trim();
      const kanaQuery = firstNameKanaSearch.trim();
      const phoneQuery = toHalfWidth(phoneSearch.trim());
      const customerCodeQuery = searchCustomerCodeInput.trim();
      GetSearchCustomerRegistrationFilterListService(
        searchQuery,
        showInvalidStores,
        kanaQuery,
        phoneQuery,
        sorting.orderBy,
        sorting.column,
        customerCodeQuery,
        advanceSearchValues,
        advanceSearchValues.isAdvanceSearchActive
      );
    // }
  }

  useEffect(() => {
    if (
      freeWordSearch === "" &&
      !showInvalidStores &&
      firstNameKanaSearch === "" &&
      phoneSearch === ""
    ) {
      resetCustomerFilterDetail();
    }
  }, [
    freeWordSearch,
    resetCustomerFilterDetail,
    showInvalidStores,
    firstNameKanaSearch,
    phoneSearch,
  ]);

  // First useEffect - handle customerIdNo changes
  useEffect(() => {
    if (customerIdNo > 0) {
      openAddCustomerHandler({ Id: customerIdNo });
      // Only clear state if we're on the customer management page
      if (location.pathname === RoutesConstants.CustomerManagement) {
        history.replace({
          pathname: location.pathname,
          state: undefined,
        });
      }
      setCustomerIdNo(-1); // Reset after handling
      resetCustomerDetail();
    }
  }, [
    customerIdNo,
    history,
    location.pathname,
    openAddCustomerHandler,
    resetCustomerDetail,
  ]);

  useEffect(() => {
    if (!pageInfo.isPagerChange && !pageLimitInfo.isPageLimitChange) {
      callGetCustomerListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    freeWordSearch,
    firstNameKanaSearch,
    phoneSearch,
    searchCustomerCodeInput,
    showInvalidStores,
    sorting.orderBy,
    sorting.column,
    advanceSearchValues
  ]);
  
  useEffect(() => {
    return(()=>{
      clearAllFilters();
      resetAdvanceSearchValues();
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetCustomerListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: customerList?.PagingDetails?.TotalPages,
      isPagerChange: false,
      isRefreshPage: false
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCustomer]);

  /**
   * FIXED: The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageInfo.isPagerChange" of the useEffect changed.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetCustomerListService();
      // Reset the flag after API call
      setPageInfo(prev => ({ ...prev, isPagerChange: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.isPagerChange]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateCustomerState.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCustomerState.success) {
      resetAddUpdateCustomerState();
      callGetCustomerListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateCustomerState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteCustomerStateSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteCustomerState.success) {
      resetDeleteCustomer();
      if (allCustomer?.length == 1) {
        if (pageInfo.currentPage > 1)
          setPageInfo({
            ...pageInfo,
            currentPage: 1,
            isRefreshPage: true,
            isPagerChange: true,
          });
        else callGetCustomerListService();
      } else callGetCustomerListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCustomerState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetCustomerListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This function deletes the user by providing Id to the Api
   */
  const deleteHandler = () => {
    DeleteCustomerService(selectedId);
  };

  const columns = getColumns<any, number>(
    [
      {
        columnType: ColumnType.Hidden,
        accessorKey: "Id",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          const firstName = row.FirstName || "";
          const lastName = row.LastName || "";
          return (
            <>
              <p className="text-wrap">{`${row.LastNameKana} ${row.FirstNameKana}`}</p>
              {(lastName || firstName) &&
                <p className="text-wrap">{`${lastName} ${firstName}`}</p>
              }
            </>
          );
        },
        accessorKey: "FirstName",
        displayName: "name",
        sortingName: "last_name_kana",
      },

      {
        columnType: ColumnType.TextHeader,
        accessorKey: "Phone1",
        displayName: "contact_number",
      },
      {
        columnType: ColumnType.Sorting,
        accessorKey: "PrefectureName",
        sortingName: "prefecture_name",
        displayName: "prefecture_name",
      },
      // will we use later
      {
        columnType: ColumnType.Sorting,
        accessorKey: "LastVisitTime",
        displayName: "LastVisitTime",
        sortingName: "last_payment_date",
      },

      {
        columnType: ColumnType.JsxElement,
        headerClassName: "last-column",
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <div className="d-flex gap-2 flex-wrap ">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  openAddCustomerHandler(row);
                }}
              >
                <i className="bi bi-eye me-2"></i>
                <FormattedMessage id="see_details" />
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const userType = getUserTypeFromLocalStorage();
                  let locationState = {
                    customerId: row.Id,
                    storeId:
                      userType === userTypeEnum[userTypeEnum.HeadQuarter] ||
                      userType === userTypeEnum[userTypeEnum.SuperAdmin]
                        ? ""
                        : getStoreIdFromLocalStorage(),
                    from: RoutesConstants.CustomerManagement
                  };
                  localStorage.setItem("lizera_smr_info", JSON.stringify(locationState));
                  history.push({
                    pathname: RoutesConstants.SalesMedicalRecords,
                    state: {
                      customerId: row.Id,
                      storeId:
                        userType === userTypeEnum[userTypeEnum.HeadQuarter] ||
                        userType === userTypeEnum[userTypeEnum.SuperAdmin]
                          ? ""
                          : getStoreIdFromLocalStorage(),
                      from: RoutesConstants.CustomerManagement
                    },
                  });
                }}
              >
                <i className="bi bi-clipboard2-pulse me-2"></i>
                <FormattedMessage id="sales_medical_record" />
              </Button>
            </div>
          );
        },
        accessorKey: "isEdit",
        displayName: "",
      },
    ],
    sorting,
    handleSortingChange
  );

  const addPopupCloseHandler = () => {
    props.addToggleHandler();
  };

  const openAdvanceSearchHandler = (values: any) => {
    props.advanceSearchPopupToggleHandler();
  };

  const clearAllFilters = () => {
    resetCustomerFilterDetail();
    setShowInvalidStores(false);
    setFirstNameKanaSearch("");
    setKanaInput("");
    setPhoneSearch("");
    setPhoneInput("");
    setFreeWordSearch("");
    setSearchInput("");
    setCustomerCodeInput("");
    setSearchCustomerCodeInput("");
    resetAdvanceSearchValues();
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  }
  
  // const clearAdvanceSearchHandler = (values: any) => {
  //   resetAdvanceSearchValues();
  // };

  const advanceSearchSubmitHandler = (values: any) => {
    clearAllFilters();
    setAdvanceSearchValues({...values, isAdvanceSearchActive: true});
    props.advanceSearchPopupToggleHandler();
  };

  return (
    <React.Fragment>
      <FormLoader
        loading={
          inProgress ||
          addUpdateCustomerState.inProgress ||
          deleteCustomerState.inProgress ||
          advanceSearchState.inProgress
        }
      />

      {props.showAddPopup && (
        <AddEditCustomerManagement
          id={selectedId}
          viewOnly={false}
          modalClosed={addPopupCloseHandler}
        />
      )}

      {props.showDeletePopup && (
        <DeletePopup
          title="delete_User"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_user_confirm"
          isLoading={deleteCustomerState.inProgress}
        />
      )}
      {props.showAdvanceSearchPopup && (
        <AdvanceSearchCustomer submitHandler={advanceSearchSubmitHandler} modalClosed={props.advanceSearchPopupToggleHandler}/>
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="customer_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="customer_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row g-1 align-items-center mb-3">
          <div className="col-lg-12 search_box">
            <div className="row  g-2">
              <div className="col-md-3 col-sm-6 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleSearchClick}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_free_word")}
                    value={searchInput}
                    disabled={advanceSearchValues.isAdvanceSearchActive}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearchClick();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="col-md-3 col-sm-6 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleKanaSearch}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_kana")}
                    value={kanaInput}
                    disabled={advanceSearchValues.isAdvanceSearchActive}
                    onChange={(e) => {
                      const value = e.target.value;
                      // if (stringJpRegex.test(value)) {
                        setKanaInput(value);
                      // }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleKanaSearch();
                      }
                    }}
                  />
                </div>
              </div>

              <div className="col-md-3 col-sm-6 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handlePhoneSearch}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_phone")}
                    disabled={advanceSearchValues.isAdvanceSearchActive}
                    value={phoneInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      // if (integerRegex.test(value)) {
                        setPhoneInput(value);
                      // }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handlePhoneSearch();
                      }
                    }}
                  />
                </div>
              </div>

              <div className="col-md-3 col-sm-6 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleCustomerCodeSearch}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_customer_code")}
                    value={customerCodeInput}
                    disabled={advanceSearchValues.isAdvanceSearchActive}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomerCodeInput(value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleCustomerCodeSearch();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-12 mt-2">
            <div className="d-flex justify-content-end align-items-center">
              <input
                type="checkbox"
                id="blockedCustomersCheckbox"
                className="form-check-input m-0"
                checked={showInvalidStores ? true : false}
                disabled={advanceSearchValues.isAdvanceSearchActive}
                onChange={() => handleInvalidTreatmentFilter()}
              />
              <label
                htmlFor="blockedCustomersCheckbox"
                className="form-check-label ms-1"
              >
                {formatMessage("blocked_customers")}
              </label>
              {(getCustomerFilter.IsFilterActive || advanceSearchValues.isAdvanceSearchActive) && (
                <button
                  className={`ms-1 btn ${
                    (getCustomerFilter.IsFilterActive || advanceSearchValues.isAdvanceSearchActive)
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={clearAllFilters}
                  title={formatMessage(advanceSearchValues.isAdvanceSearchActive ? "clear_advance_search" : "clear_filter")}
                >
                  <svg
                    id="fi_3126668"
                    enableBackground="new 0 0 512 512"
                    height="18"
                    viewBox="0 0 512 512"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#ffffff"
                  >
                    <g>
                      <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                      <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                    </g>
                  </svg>
                </button>
              )}
              {
                userTypefromLocalStorage !== userTypeEnum[userTypeEnum.SuperAdmin] && 
                <button
                  className={`btn btn-info ms-3 ${!(allCustomer.length > 0) ? "cursor-not-allowed" : ""}`}
                  onClick={() => {
                    callCSVOutput();
                  }}
                  disabled={!(allCustomer.length > 0)}
                >
                  <i className="bi bi-download me-1"></i>
                  <FormattedMessage id="csv_output" />
                </button>
              }
              {getUserTypeFromLocalStorage() !== "SuperAdmin" &&
                !advanceSearchValues.isAdvanceSearchActive &&
                  <button
                    className="btn btn-secondary ms-3"
                    onClick={openAdvanceSearchHandler}
                  >
                    {formatMessage("advance_search")}
                  </button>
              }
              {isOperationPermittedRoleBase(
                ModuleType.CustomerManagement,
                ActionType.Add
              ) && (
                <button
                  className="btn btn-primary ms-3"
                  onClick={() => {
                    openAddCustomerHandler({ Id: -1 });
                  }}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  {formatMessage("add_customer")}
                </button>
              )}
            </div>
          </div>
        </div>
        {allCustomer.length > 0 ? (
          <div className={`${openPanel ? "panelOpen" : ""}`}>
            <div>
              <div className="card changingPanel">
                <div className="tableDesign row-eq-height tableCustomers">
                  <div className="table-responsive" data-testid="table">
                    {allCustomer && (
                      <Grid
                        data={allCustomer}
                        columns={columns}
                        isInProgress={false}
                        hiddenColumns={["Id"]}
                      />
                    )}
                  </div>
                </div>
              </div>
              {allCustomer.length > 0 ? (
                <div className="d-flex align-items-center justify-content-center p-2 tableFooter">
                  <Pagination
                    isRefreshPage={pageInfo.isRefreshPage}
                    totalPages={pageInfo.totalPages}
                    pageLimit={pageLimitInfo.pageLimit}
                    onPageChanged={onPageChanged}
                    disablePagination={disablePagination}
                    setIsRefreshPage={setIsRefreshPage}
                  />

                  <select
                    data-testid="pageSelect"
                    className="pageLimit ms-1"
                    onChange={onPageLimitChanged}
                    value={pageLimitInfo.pageLimit}
                  >
                    {pageLimitOptions.map((op) => (
                      <option key={op} value={op}>
                        {op} {formatMessage("rows")}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        ) : (
          <div className="message text-center">
            <img src={noMessageIcon} alt="" className="no_message" />
            {freeWordSearch.trim().length > 0 ||
            phoneSearch?.trim().length > 0 ||
            firstNameKanaSearch?.trim().length > 0 ||
            showInvalidStores || advanceSearchValues.isAdvanceSearchActive ? (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_data_available" />
                </p>
              </>
            ) : (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_customer" />
                </p>
                <p className="mb-3">{formatMessage("no_customer_message")}</p>
                {isOperationPermittedRoleBase(
                  ModuleType.CustomerManagement,
                  ActionType.Add
                ) && (
                  <LoaderButton
                    text="add_customer"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      openAddCustomerHandler({ Id: -1 })
                    }
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AdvanceSearchPopupHOC(AddPopupHOC(
  ShowPopupHOC(DeletePopupHOC(observer(CustomerManagement)))
));
